import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Zap, Hammer, Package, Wrench, Mountain, Lightbulb } from 'lucide-react';
import { companyInfo, cordisteServices } from '../data/mock';

const iconMap = {
  Zap: Zap,
  Hammer: Hammer,
  Package: Package,
  Wrench: Wrench,
  Lightbulb: Lightbulb
};

export const Cordiste = () => {
  React.useEffect(() => {
    // Scroll to section if hash is present
    if (window.location.hash) {
      const element = document.getElementById(window.location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-32 pb-20">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1766448979403-8a071dae1619"
            alt="Cordiste"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/60" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full mb-6">
              <Mountain className="w-4 h-4 text-blue-400" />
              <span className="text-blue-300 text-sm font-medium">Travaux en Hauteur</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Services Cordiste
            </h1>
            
            <p className="text-xl text-slate-300 mb-8">
              Expert en travaux d'accès difficile par techniques de corde. Interventions sécurisées pour tous vos projets en hauteur au Pays Basque.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-1 group"
              >
                Demander un devis
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800/80 backdrop-blur-sm hover:bg-slate-700 text-white border border-slate-700 rounded-xl font-semibold transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Nos Prestations Cordiste
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Des solutions adaptées pour tous vos travaux en hauteur
            </p>
          </div>

          <div className="grid gap-8 max-w-6xl mx-auto">
            {cordisteServices.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  id={service.id}
                  className="group bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-blue-500/10"
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    {/* Image */}
                    <div className={`relative h-80 md:h-auto overflow-hidden ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent md:bg-gradient-to-r" />
                    </div>

                    {/* Content */}
                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <div className="w-14 h-14 bg-blue-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition-colors">
                        <IconComponent className="w-7 h-7 text-blue-400" />
                      </div>

                      <h3 className="text-3xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors">
                        {service.title}
                      </h3>
                      
                      <p className="text-lg text-slate-300 mb-6">
                        {service.description}
                      </p>

                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-blue-400 font-semibold hover:gap-4 transition-all group/link"
                      >
                        Demander un devis
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
              Pourquoi Choisir un Cordiste ?
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
                <h3 className="text-xl font-bold text-white mb-3">Accès Difficile</h3>
                <p className="text-slate-400">
                  Intervention possible là où les échafaudages et nacelles ne peuvent pas accéder. Idéal pour les façades, toitures et structures complexes.
                </p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
                <h3 className="text-xl font-bold text-white mb-3">Économique</h3>
                <p className="text-slate-400">
                  Pas besoin d'échafaudage coûteux ni de location de nacelle. Installation rapide et coûts réduits pour votre projet.
                </p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
                <h3 className="text-xl font-bold text-white mb-3">Rapidité</h3>
                <p className="text-slate-400">
                  Mise en place rapide et intervention immédiate. Gain de temps considérable sur vos chantiers.
                </p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-blue-500/50 transition-all">
                <h3 className="text-xl font-bold text-white mb-3">Sécurité</h3>
                <p className="text-slate-400">
                  Équipements professionnels certifiés et respect des normes de sécurité les plus strictes pour chaque intervention.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-900/30 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Un Projet en Hauteur ?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Devis gratuit et sans engagement. Intervention rapide sur tout le Pays Basque.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-slate-100 text-blue-600 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group"
                >
                  Demander un devis gratuit
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-700 hover:bg-blue-600 text-white border-2 border-blue-500 rounded-xl font-semibold transition-all duration-300"
                >
                  <Phone className="w-5 h-5" />
                  {companyInfo.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
